// Manual fog of war (issue 851; design/22-hidden-means-absent.md, binding).
//
// Fog is NOT an overlay a player's browser draws over the whole map. It is a
// part of the one visibility rule (`canSee` in objects.ts): with the fog on,
// a thing every square of whose footprint is covered is ABSENT for a player —
// not greyed, not flagged — and the map picture a player fetches has every
// covered square painted black by the server (vtt/server/src/fogMask.ts).
// Revealing sends what it uncovers; covering again takes it away.
//
// Per scene, in the scene's state. Squares are grid INDICES on the fitted
// grid, as focuses and areas use (`axisPositions` in grid/cells.ts: the
// board's own edges are the outer lines, so the partial squares at an edge
// are squares too and nothing on the board lies outside the grid). What is
// revealed is kept as normalised ROW SPANS — sorted by row then column,
// merged, never overlapping — so a whole map revealed is one span a row.
//
// The extent (the map's size in pixels) is the server's to state: every fog
// event is STAMPED with it when committed, so replaying a log computes the
// same squares the table saw. A thing the viewer owns, and any player
// character, is never hidden by fog; the DM always sees everything.
import { EMPTY_GRID } from '../assets/pictures.js';
import { axisPositions, cellIndexAt, footprintAt } from '../grid/cells.js';
/** At most this many spans are kept: a change that would exceed it is refused (`fogRefusal`). Indices beyond `index` are refused by the parser. */
export const FOG_LIMITS = { spans: 4000, index: 2000, extent: 100_000 };
export const FOG_TOO_MANY = 'The fog cannot hold that many separate patches. Reveal or cover a larger area instead.';
export const emptyFog = () => ({ on: false, revealed: [], rev: 0, extent: null });
/** The board's size when there is no map (plan §2.10). */
export const EMPTY_EXTENT = { width: EMPTY_GRID.squares * EMPTY_GRID.squarePx, height: EMPTY_GRID.squares * EMPTY_GRID.squarePx };
export const FOG_KINDS = ['fog.set', 'fog.reveal', 'fog.cover', 'fog.revealAll', 'fog.coverAll'];
const isIndex = (v) => typeof v === 'number' && Number.isInteger(v) && v >= 0 && v <= FOG_LIMITS.index;
const isSide = (v) => typeof v === 'number' && Number.isFinite(v) && v > 0 && v <= FOG_LIMITS.extent;
function parseExtent(v) {
    if (typeof v !== 'object' || v === null)
        return null;
    const { width, height } = v;
    return isSide(width) && isSide(height) ? { width, height } : null;
}
export function parseRect(v) {
    if (typeof v !== 'object' || v === null)
        return null;
    const { i0, j0, i1, j1 } = v;
    if (!isIndex(i0) || !isIndex(j0) || !isIndex(i1) || !isIndex(j1) || i0 > i1 || j0 > j1)
        return null;
    return { i0, j0, i1, j1 };
}
/** Strict: whole indices, corners in order, bounded. `extent` is read when present (a stored event carries the server's stamp). */
export function parseFogBody(b) {
    const extent = b.extent === undefined ? undefined : parseExtent(b.extent);
    if (extent === null)
        return null;
    const stamp = extent ? { extent } : {};
    switch (b.kind) {
        case 'fog.set': return typeof b.on === 'boolean' ? { kind: 'fog.set', on: b.on, ...stamp } : null;
        case 'fog.reveal':
        case 'fog.cover': {
            const rect = parseRect(b.rect);
            return rect ? { kind: b.kind, rect, ...stamp } : null;
        }
        case 'fog.revealAll':
        case 'fog.coverAll': return { kind: b.kind, ...stamp };
        default: return null;
    }
}
/** Sorted, merged spans from any list (adjacent squares join). */
export function normaliseSpans(spans) {
    const sorted = [...spans].sort((a, b) => a.j - b.j || a.i0 - b.i0);
    const out = [];
    for (const s of sorted) {
        const last = out[out.length - 1];
        if (last && last.j === s.j && s.i0 <= last.i1 + 1)
            last.i1 = Math.max(last.i1, s.i1);
        else
            out.push({ j: s.j, i0: s.i0, i1: s.i1 });
    }
    return out;
}
/** The squares in both lists of spans. */
export function intersectSpans(a, b) {
    const rows = new Map();
    for (const s of normaliseSpans(b)) {
        const r = rows.get(s.j);
        if (r)
            r.push(s);
        else
            rows.set(s.j, [s]);
    }
    const out = [];
    for (const s of normaliseSpans(a))
        for (const t of rows.get(s.j) ?? []) {
            const i0 = Math.max(s.i0, t.i0);
            const i1 = Math.min(s.i1, t.i1);
            if (i0 <= i1)
                out.push({ j: s.j, i0, i1 });
        }
    return normaliseSpans(out);
}
/** The spans with the box revealed. */
export function revealSpans(spans, rect) {
    const added = [];
    for (let j = rect.j0; j <= rect.j1; j += 1)
        added.push({ j, i0: rect.i0, i1: rect.i1 });
    return normaliseSpans([...spans, ...added]);
}
/** The spans with the box covered again. */
export function coverSpans(spans, rect) {
    const out = [];
    for (const s of spans) {
        if (s.j < rect.j0 || s.j > rect.j1 || s.i1 < rect.i0 || s.i0 > rect.i1) {
            out.push({ ...s });
            continue;
        }
        if (s.i0 < rect.i0)
            out.push({ j: s.j, i0: s.i0, i1: rect.i0 - 1 });
        if (s.i1 > rect.i1)
            out.push({ j: s.j, i0: rect.i1 + 1, i1: s.i1 });
    }
    return out;
}
export const isRevealed = (spans, i, j) => spans.some((s) => s.j === j && s.i0 <= i && i <= s.i1);
/** Whether any square of a box is revealed. */
export const anyRevealed = (spans, rect) => spans.some((s) => s.j >= rect.j0 && s.j <= rect.j1 && s.i0 <= rect.i1 && s.i1 >= rect.i0);
/** How many squares the board has across and down: the cells between consecutive lines, the board's edges included. */
export function boardSquares(grid, extent) {
    return { cols: Math.max(0, axisPositions(grid, extent, 'x').length - 1), rows: Math.max(0, axisPositions(grid, extent, 'y').length - 1) };
}
/** The box clipped to the board, or null when none of it is on it. */
function clip(rect, cols, rows) {
    const r = { i0: rect.i0, j0: rect.j0, i1: Math.min(rect.i1, cols - 1), j1: Math.min(rect.j1, rows - 1) };
    return r.i0 > r.i1 || r.j0 > r.j1 ? null : r;
}
// Explored squares and doors as last seen (issue 1144, slice 2; design/30 §3), read loosely from anywhere.
const GROUP_RE = /^(party|[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})$/i;
/** An explored record's key: a group, and (qa-runs#1160, `floorFog.ts`) `@<floorId>` for a floor other than the ground. */
const EXPLORED_KEY_RE = /^(party|[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})(@[a-z0-9-]{1,40})?$/i;
/** Spans read loosely from anywhere, normalised; a malformed one is dropped. */
export function readSpans(v) {
    if (!Array.isArray(v))
        return [];
    return normaliseSpans(v.filter((s) => {
        if (typeof s !== 'object' || s === null)
            return false;
        const { j, i0, i1 } = s;
        return isIndex(j) && isIndex(i0) && isIndex(i1) && i0 <= i1;
    }).slice(0, FOG_LIMITS.spans));
}
export function readExplored(v) {
    const out = {};
    if (typeof v !== 'object' || v === null || Array.isArray(v))
        return out;
    for (const [g, s] of Object.entries(v))
        if (EXPLORED_KEY_RE.test(g))
            out[g] = readSpans(s);
    return out;
}
export function readDoorsSeen(v) {
    const out = {};
    if (typeof v !== 'object' || v === null || Array.isArray(v))
        return out;
    for (const [g, doors] of Object.entries(v)) {
        if (!GROUP_RE.test(g) || typeof doors !== 'object' || doors === null || Array.isArray(doors))
            continue;
        const d = {};
        for (const [id, open] of Object.entries(doors))
            if (GROUP_RE.test(id) && id !== 'party' && typeof open === 'boolean')
                d[id] = open;
        out[g] = d;
    }
    return out;
}
/** Fog from an older snapshot, or none: spans read loosely (a malformed one is dropped) and normalised. */
export function normaliseFog(v) {
    if (typeof v !== 'object' || v === null)
        return emptyFog();
    const f = v;
    const spans = Array.isArray(f.revealed) ? f.revealed.filter((s) => {
        if (typeof s !== 'object' || s === null)
            return false;
        const { j, i0, i1 } = s;
        return isIndex(j) && isIndex(i0) && isIndex(i1) && i0 <= i1;
    }) : [];
    return {
        on: f.on === true,
        revealed: normaliseSpans(spans),
        rev: typeof f.rev === 'number' && Number.isInteger(f.rev) && f.rev >= 0 ? f.rev : 0,
        extent: parseExtent(f.extent),
        ...(f.explored !== undefined ? { explored: readExplored(f.explored) } : {}),
        ...(f.doorsSeen !== undefined ? { doorsSeen: readDoorsSeen(f.doorsSeen) } : {}),
    };
}
/** The DM's cover takes squares out of every group's explored set as well (design/30 §3.4): covered stays covered until a character sees it again. */
function coverExplored(explored, rect) {
    return Object.fromEntries(Object.entries(explored).map(([g, spans]) => [g, rect ? coverSpans(spans, rect) : []]));
}
/** Apply an accepted fog event. The squares a box or "all" names are the board's at the stamped extent. */
export function reduceFog(fog, body, grid) {
    const extent = body.extent ?? fog.extent;
    const { cols, rows } = boardSquares(grid, extent ?? EMPTY_EXTENT);
    const next = (patch) => ({ ...fog, extent, rev: fog.rev + 1, ...patch });
    switch (body.kind) {
        case 'fog.set': return next({ on: body.on });
        case 'fog.reveal': {
            const r = clip(body.rect, cols, rows);
            return next({ revealed: r ? revealSpans(fog.revealed, r) : fog.revealed });
        }
        case 'fog.cover': return next({ revealed: coverSpans(fog.revealed, body.rect), ...(fog.explored ? { explored: coverExplored(fog.explored, body.rect) } : {}) });
        case 'fog.revealAll': return next({ revealed: cols > 0 && rows > 0 ? revealSpans([], { i0: 0, j0: 0, i1: cols - 1, j1: rows - 1 }) : [] });
        case 'fog.coverAll': return next({ revealed: [], ...(fog.explored ? { explored: coverExplored(fog.explored, null) } : {}) });
        default: return fog;
    }
}
/** Why an accepted-looking fog change is refused: the spans it would leave exceed the cap. */
export function fogRefusal(fog, body, grid) {
    return reduceFog(fog, body, grid).revealed.length > FOG_LIMITS.spans ? FOG_TOO_MANY : null;
}
/** The squares a footprint of `size` centred at `centre` touches, as a box of indices (the existing footprint helper, read as squares). */
export function footprintSquares(grid, extent, centre, size) {
    const r = footprintAt(grid, extent, centre, size);
    const xs = axisPositions(grid, extent, 'x');
    const ys = axisPositions(grid, extent, 'y');
    // An edge exactly on a line belongs to the square it bounds, not the next one: step half a pixel (or half the footprint) inside.
    const ex = Math.min(0.5, Math.abs(r.width) / 2);
    const ey = Math.min(0.5, Math.abs(r.height) / 2);
    return { i0: cellIndexAt(xs, r.x + ex), i1: cellIndexAt(xs, r.x + r.width - ex), j0: cellIndexAt(ys, r.y + ey), j1: cellIndexAt(ys, r.y + r.height - ey) };
}
/** Whether the fog hides a thing standing at `at` with `size`: on, and every square of its footprint covered. Ownership and player characters are `canSee`'s. */
export function fogHides(at, size, scope) {
    const { fog, grid } = scope;
    if (!fog.on)
        return false;
    return !anyRevealed(fog.revealed, footprintSquares(grid, fog.extent ?? EMPTY_EXTENT, at, size));
}
/** The fog as a viewer may know it: the whole for the DM; for a player whether it is on and, while on, what is revealed. */
export function projectFog(fog, role) {
    if (role === 'dm')
        return { ...fog, revealed: fog.revealed.map((s) => ({ ...s })), extent: fog.extent ? { ...fog.extent } : null, ...(fog.explored ? { explored: Object.fromEntries(Object.entries(fog.explored).map(([g, s]) => [g, s.map((x) => ({ ...x }))])) } : {}), ...(fog.doorsSeen ? { doorsSeen: Object.fromEntries(Object.entries(fog.doorsSeen).map(([g, d]) => [g, { ...d }])) } : {}) };
    return { on: fog.on, revealed: fog.on ? fog.revealed.map((s) => ({ ...s })) : [], rev: fog.rev };
}
//# sourceMappingURL=fog.js.map